import { createContext, useContext, useEffect, useState, ReactNode } from "react"
import useWebSocket, { ReadyState, SendMessage } from "react-use-websocket"
import { useAuth } from "@clerk/clerk-react"
import { useStore } from "@/store/store"
import { api } from "./api"
import { WebSocketLike } from "react-use-websocket/dist/lib/types"
import { Detection } from "@/store/detection-slice"
import { toast } from "sonner"
import { useMap } from "react-map-gl/maplibre"

interface IWebSocketContext {
    sendMessage: SendMessage
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    lastMessage: MessageEvent<any> | null
    readyState: ReadyState
    getWebSocket: () => WebSocketLike | null
    lastJsonMessage: unknown
}

const WebSocketContext = createContext<IWebSocketContext | undefined>(undefined)

interface WebSocketProviderProps {
    children: ReactNode
}

/**
 * 	ID          uuid.UUID `json:"id"`
	Title       string    `json:"title"`
	Description string    `json:"description"`
	UserID      string    `json:"user_id"`
	CreatedAt   time.Time `json:"created_at"`
 */
type Alert = {
    id: string
    title: string
    description: string
    user_id: string
    created_at: string
}

type WebsocketMessage = {
    action: "detection" | "violation" | "alert"
    data: Detection | Alert
}

export const WebSocketProvider = ({ children }: WebSocketProviderProps) => {
    const socketUrlBase = api.defaults.baseURL?.replace(/^http/, "ws").replace(/^https/, "wss")
    const socketUrl = socketUrlBase + "/v0/realtime/detections"

    // Clerk hooks to retrieve the session ID and a fresh token
    const { sessionId, getToken } = useAuth()

    // Keep track of the token in local state
    const [token, setToken] = useState<string | null>(null)

    // Store actions
    const addDetection = useStore((s) => s.addDetection)
    const addDrone = useStore((s) => s.addDrone)

    const trackedDrone = useStore((s) => s.trackedDrone)
    const map = useMap()
    /**
     * Whenever the Clerk session changes (e.g., user logs in/out, token refreshes),
     * fetch a new token and store it in state.
     */
    useEffect(() => {
        const fetchToken = async () => {
            // If there's no active session, clear the token so we won't connect
            if (!sessionId) {
                setToken(null)
                return
            }
            // Otherwise, fetch a fresh token from Clerk
            const freshToken = await getToken()
            setToken(freshToken)
        }

        fetchToken()
    }, [sessionId, getToken])

    /**
     * Pass the token in the WebSocket URL (e.g., ?token=xxx).
     * react-use-websocket will automatically close and re-instantiate
     * the connection if this URL changes.
     */
    const urlWithToken = token ? `${socketUrl}?token=${token}` : null

    const {
        sendMessage,
        lastMessage,
        readyState,
        getWebSocket,
        lastJsonMessage,
    } = useWebSocket(urlWithToken, {
        shouldReconnect: () => true,
        retryOnError: true,
        heartbeat: true,
        reconnectAttempts: Infinity,
        reconnectInterval: 5000,
        onOpen: () => {
        },
        onMessage: (message) => {
            try {
                
                const messageBody = JSON.parse(message.data) as WebsocketMessage
                if (messageBody.action === "detection") {

                    const detection = messageBody.data as Detection
                    addDetection(detection)
                    addDrone(detection.drone_identifier)

                    if (trackedDrone?.drone_identifier === detection.drone_identifier) {
                        map?.current?.flyTo({
                            center: [detection.longitude, detection.latitude],
                            zoom: 14,
                        });
                    }
                } else if (messageBody.action === "violation") {
                    // addViolation(detection.data)
                } else if (messageBody.action === "alert") {
                    const alert = messageBody.data as Alert
                    // Only alert if it was created in the last 30 seconds
                    if (alert.created_at > new Date(Date.now() - 30000).toISOString()) {
                        toast.info(alert.title, {
                            richColors: true,
                            description: alert.description,
                        })
                    }
                }
            } catch (error) {
                if (process.env.NODE_ENV === "development") {
                    console.error("Error processing websocket message", error)
                }
            }
        },
        onError: async () => {
            // get a new token
            const freshToken = await getToken()
            setToken(freshToken)
        },
    })

    const contextValue: IWebSocketContext = {
        sendMessage,
        lastMessage,
        readyState,
        getWebSocket,
        lastJsonMessage,
    }

    return (
        <WebSocketContext.Provider value={contextValue}>
            {children}
        </WebSocketContext.Provider>
    )
}

export const useSocket = () => {
    const context = useContext(WebSocketContext)
    if (context === undefined) {
        throw new Error("useSocket must be used within a WebSocketProvider")
    }
    return context
}
